import { HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import {
  ExternalIngestionFolderFragment,
  InternalIngestionFolderFragment,
} from '@client/graphql/__generated__/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, ReactNode } from 'react';

dayjs.extend(relativeTime);

interface FolderInformationProps {
  folder?:
    | ExternalIngestionFolderFragment
    | InternalIngestionFolderFragment
    | null;
}

const debugTextProps = {
  color: 'compliance.debug.text',
  fontSize: 'xs',
} as const;

const DebugText: FC<{ children: ReactNode; opacity?: number }> = ({
  children,
  opacity,
}) => (
  <Text {...debugTextProps} opacity={opacity}>
    {children}
  </Text>
);

const KeyValueRow: FC<{ label: string; value: string | ReactNode }> = ({
  label,
  value,
}) => (
  <HStack justify="space-between">
    <DebugText opacity={0.7}>{label}:</DebugText>
    {typeof value === 'string' ? <DebugText>{value}</DebugText> : value}
  </HStack>
);

export const FolderInformation: FC<FolderInformationProps> = ({ folder }) => {
  return (
    <VStack align="stretch" spacing={2}>
      <KeyValueRow
        label="Folder Updated At"
        value={
          folder?.folderUpdatedAt ? (
            <Tooltip
              label={new Date(
                folder.folderUpdatedAt as string
              ).toLocaleString()}
            >
              <Text
                {...debugTextProps}
                cursor="help"
                textDecoration="underline"
                textDecorationStyle="dotted"
              >
                {dayjs(folder.folderUpdatedAt as string).fromNow()}
              </Text>
            </Tooltip>
          ) : (
            'not available'
          )
        }
      />

      <KeyValueRow
        label="Client Type"
        value={
          folder?.transactionPipeline?.clientType ||
          'unknown - no pipeline match'
        }
      />
      <KeyValueRow
        label="Property Type"
        value={
          folder?.transactionPipeline?.transactionPropertyType ||
          'unknown - no pipeline match'
        }
      />
      <KeyValueRow
        label="State"
        value={
          folder?.transactionPipeline?.usState || 'unknown - no pipeline match'
        }
      />

      <KeyValueRow label="Stage" value={folder?.stage || 'not set'} />

      <KeyValueRow
        label="Fully Reprocessed At"
        value={
          folder && 'fullyReprocessedAt' in folder ? (
            folder.fullyReprocessedAt ? (
              <Tooltip
                label={new Date(
                  folder.fullyReprocessedAt as string
                ).toLocaleString()}
              >
                <Text
                  {...debugTextProps}
                  cursor="help"
                  textDecoration="underline"
                  textDecorationStyle="dotted"
                >
                  {dayjs(folder.fullyReprocessedAt as string).fromNow()}
                </Text>
              </Tooltip>
            ) : (
              'never'
            )
          ) : (
            'not available'
          )
        }
      />
    </VStack>
  );
};
